import { useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { LANG_LABELS, editsCatalog, exportSheets, planChanges, planImport, planSummary, templateSheets, textLabel } from "@speisekarte/core";
import { phrasesOfData } from "./TextWork.tsx";
import type { ImportPlan, Sheet } from "@speisekarte/core";
import { plainSheets, readWorkbook, writeWorkbook } from "../excel.ts";
import { api, messageOf, store, useStore } from "../store.ts";
import { useKept } from "../kept.ts";

const today = () => new Date().toLocaleDateString("sv-SE");

/**
 * „Excel“: the catalogue as a workbook (docs/01-architektur.md, Excel-Mappe): the template, the
 * whole catalogue, and an upload with a preview before anything is written, as in KellerLog.
 */
export function Import() {
  const s = useStore();
  const data = s.data!;
  const canEdit = editsCatalog(s.role);
  const [sheets, setSheets] = useKept<{ sheets: Sheet[]; file: string } | null>("import", null);
  const [state, setState] = useState<{ ok?: string; error?: string; busy?: boolean }>({});
  const plan = useMemo(
    () => (sheets ? planImport(sheets.sheets, { dishes: data.dishes, allergens: data.allergens, categories: data.categories, drinkCategories: data.drinkCategories, drinkStart: data.settings.drinkStart, texts: data.texts, phrases: data.phrases ?? {} }) : null),
    [sheets, data.dishes, data.allergens, data.categories, data.drinkCategories, data.settings.drinkStart, data.texts, data.phrases],
  );
  const house = (data.settings.house || data.workspace).replace(/[\\/:*?"<>|]+/g, " ");

  async function upload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setState({});
    try {
      setSheets({ sheets: plainSheets(await readWorkbook(file)), file: file.name });
    } catch (err) {
      setState({ error: `Die Datei ließ sich nicht lesen: ${messageOf(err)}` });
    }
  }

  async function apply() {
    if (!sheets) return;
    setState({ busy: true });
    try {
      const r = await store.change(() => api<{ result: { added: number; changed: number; allergens: number; categories: number; texts?: number; phrases?: number }; errors: unknown[] }>("/api/import", { body: { sheets: sheets.sheets } }));
      setSheets(null);
      const texts = r.result.texts ?? 0;
      const words = r.result.phrases ?? 0;
      setState({ ok: `Übernommen: ${r.result.added} neu, ${r.result.changed} geändert${r.result.categories ? `, ${r.result.categories} ${r.result.categories === 1 ? "Kategorie" : "Kategorien"}` : ""}${r.result.allergens ? `, ${r.result.allergens} Kürzel` : ""}${texts ? `, ${texts} ${texts === 1 ? "Textbaustein" : "Textbausteine"}` : ""}${words ? `, ${words} ${words === 1 ? "Übersetzung" : "Übersetzungen"}` : ""}.` });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  return (
    <div className="import">
      <p>
        <a className="back" href="#/kataloge">← Kataloge</a>
      </p>
      <section className="card">
        <h2>Speisen und Getränke als Excel-Mappe</h2>
        <p className="hint">Die Blätter „Speisen“, „Getränke“, „Kategorien“, „Allergene“, „Textbausteine“ und „Erklärungen“ mit den Regeln je Spalte. Herunterladen, in Excel oder Numbers ändern, wieder hochladen. Getränke haben dieselben Spalten wie Speisen, die Menge gehört zum Preis („0,3 l“, weitere Mengen unter „Weitere Preise“). Eine neue Kategorie gehört ins Blatt „Kategorien“, für Getränke mit „Getränke“ in der Spalte „für“. Zeilen ohne Nummer und Preis wie „Tapa del día – Fragen Sie nach unseren Tagestapas!“ stehen im Blatt „Textbausteine“.{(data.settings.languages ?? []).length > 0 && " Mit einer weiteren Sprache steht alles, was keine Speise ist (Titel, Abschnitte, Hinweise, Kategorien, Tags, Textbausteine …), im Blatt „Übersetzungen“: jeder deutsche Text einmal, eine Spalte je Sprache."}</p>
        <div className="buttons">
          <button onClick={() => void writeWorkbook(exportSheets(data.dishes, data.allergens, data.categories, data.drinkCategories, data.settings.languages ?? [], data.texts, { list: phrasesOfData(data), phrases: data.phrases ?? {} }), `Speisen ${house} ${today()}.xlsx`)} data-testid="export">
            Alle Speisen und Getränke herunterladen
          </button>
          <button onClick={() => void writeWorkbook(templateSheets(data.allergens, data.categories, data.drinkCategories), "Vorlage Speisen.xlsx")}>Leere Vorlage</button>
        </div>
        {canEdit && (
          <label className="upload">
            <span className="button primary">Excel-Mappe hochladen …</span>
            <input type="file" accept=".xlsx,.csv" onChange={upload} hidden data-testid="upload" />
          </label>
        )}
        {state.error && <p className="error-box">{state.error}</p>}
        {state.ok && <p className="ok-text">{state.ok}</p>}
      </section>
      {canEdit && (
        <section className="card" style={{ marginTop: "0.8rem" }}>
          <h2>Alte Karte einlesen</h2>
          <p className="hint">Eine Speisekarte als PDF, Word-Datei, Fotos oder Text: Das Modell liest sie, der Plan zeigt vorher, was dazukommt.</p>
          <a className="button" href="#/einlesen" data-testid="import-to-reading">
            Alte Karte einlesen …
          </a>
        </section>
      )}

      {plan && sheets && (
        <section className="card" style={{ marginTop: "0.8rem" }} data-testid="import-plan">
          <h2>{sheets.file}</h2>
          {plan.empty ? (
            <p className="error-box">In der Mappe steht kein Blatt „Speisen“, „Getränke“, „Kategorien“, „Allergene“ oder „Textbausteine“.</p>
          ) : (
            <>
              <PlanDetails plan={plan} />
              <div className="buttons">
                <button className="primary" disabled={!planChanges(plan) || state.busy} onClick={() => void apply()} data-testid="import-apply">
                  {state.busy ? "Übernehmen …" : "Übernehmen"}
                </button>
                <button className="link" onClick={() => setSheets(null)}>
                  Abbrechen
                </button>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}

/** What a plan does (S41 shows the same for an old menu read by the model): new, changed, left out. */
export function PlanDetails({ plan }: { plan: ImportPlan }) {
  return (
    <>
      <p>
        <strong>{planSummary(plan)}</strong>
      </p>
      {plan.dishes.added.length > 0 && (
        <details open={plan.dishes.added.length <= 10}>
          <summary>Neu ({plan.dishes.added.length})</summary>
          <ul className="problems">
            {plan.dishes.added.map((a) => (
              <li key={`${a.input.kind ?? "food"}${a.line}`}>
                {a.input.number} {a.input.name}
              </li>
            ))}
          </ul>
        </details>
      )}
      {plan.dishes.changed.length > 0 && (
        <details open={plan.dishes.changed.length <= 10}>
          <summary>Geändert ({plan.dishes.changed.length})</summary>
          <ul className="problems">
            {plan.dishes.changed.map((c) => (
              <li key={`${c.input.kind ?? "food"}${c.line}`}>
                {c.number} {c.name}: <span className="muted">{c.changes.join(" · ")}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {plan.categories.added.length > 0 && (
        <details open>
          <summary>Neue Kategorien ({plan.categories.added.length})</summary>
          <ul className="problems">
            {plan.categories.added.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </details>
      )}
      {plan.drinkCategories.added.length > 0 && (
        <details open>
          <summary>Neue Kategorien der Getränke ({plan.drinkCategories.added.length})</summary>
          <ul className="problems">
            {plan.drinkCategories.added.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </details>
      )}
      {(plan.allergens.added.length > 0 || plan.allergens.changed.length > 0) && (
        <details>
          <summary>Allergene und Zusatzstoffe ({plan.allergens.added.length + plan.allergens.changed.length})</summary>
          <ul className="problems">
            {plan.allergens.added.map((a) => (
              <li key={a.code}>
                neu: {a.code} {a.name}
              </li>
            ))}
            {plan.allergens.changed.map((c) => (
              <li key={c.after.code}>
                {c.after.code}: {c.before.name} → {c.after.name}
              </li>
            ))}
          </ul>
        </details>
      )}
      {(plan.texts.added.length > 0 || plan.texts.changed.length > 0) && (
        <details open={plan.texts.added.length + plan.texts.changed.length <= 10} data-testid="import-texts">
          <summary>Textbausteine ({plan.texts.added.length + plan.texts.changed.length})</summary>
          <ul className="problems">
            {plan.texts.added.map((x) => (
              <li key={x.line}>neu: {textLabel(x)}</li>
            ))}
            {plan.texts.changed.map((c) => (
              <li key={c.line}>
                {c.title}: <span className="muted">Text geändert</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {plan.phrases.changed.length > 0 && (
        <details open={plan.phrases.changed.length <= 10} data-testid="import-phrases">
          <summary>Übersetzungen ({plan.phrases.changed.length})</summary>
          <ul className="problems">
            {plan.phrases.changed.map((c) => (
              <li key={`${c.line}-${c.lang}`}>
                {c.text} → {c.after} <span className="muted">({LANG_LABELS[c.lang]}{c.before ? `, vorher „${c.before}“` : ""})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {plan.phrases.checked.length > 0 && (
        <details open={plan.phrases.checked.length <= 10} data-testid="import-checked">
          <summary>Durchgesehen ({plan.phrases.checked.length})</summary>
          <ul className="problems">
            {plan.phrases.checked.map((c) => (
              <li key={`${c.line}-${c.lang}`}>
                {c.text} <span className="muted">({LANG_LABELS[c.lang]})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {plan.errors.length > 0 && (
        <>
          <h3 className="error">Ausgelassen ({plan.errors.length})</h3>
          <ul className="problems">
            {plan.errors.map((e) => (
              <li key={`${e.sheet}${e.line}`}>
                {e.sheet}, Zeile {e.line}: {e.message}
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  );
}
